import type { AppNotification } from '../components/Preview/api';
import { fetchActivityFeed, type ActivityEvent } from '../components/Dashboard/activityFeedApi';

export function normalizeActivityMatchText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[\u201c\u201d\u2018\u2019"'`]/g, '')
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function extractQuotedActivityText(message: string): string {
  const match = message.match(/["'""'']([^"'""'']+)["'""'']/);
  return match?.[1]?.trim() || '';
}

export function isActivityTimelineNotification(notification: AppNotification): boolean {
  const msg = (notification.message || '').toLowerCase();
  if (msg.includes('liked your prompt')) return false;
  if (msg.includes('liked')) return true;
  if (msg.includes('viewed your profile')) return true;
  if (msg.includes('expanded')) return true;
  if (msg.includes('spent about') && msg.includes('profile')) return true;
  if (msg.includes('saved your profile')) return true;
  if (msg.includes('watched') && msg.includes('video')) return true;
  return false;
}

export function buildActivityDeepLinkFromNotification(notification: AppNotification): string {
  const highlightMatch = notification.action_url?.match(/[?&]highlight=(\d+)/);
  if (highlightMatch) {
    return `/activity?highlight=${highlightMatch[1]}`;
  }

  const params = new URLSearchParams();
  params.set('match', notification.message || '');
  if (notification.createdAt) {
    params.set('at', notification.createdAt);
  }
  return `/activity?${params.toString()}`;
}

export function findMatchingActivityEvent(
  items: ActivityEvent[],
  matchMessage: string,
  atIso?: string
): ActivityEvent | undefined {
  const normMatch = normalizeActivityMatchText(matchMessage);
  const quoted = extractQuotedActivityText(matchMessage);
  const atMs = atIso ? new Date(atIso).getTime() : null;

  let best: ActivityEvent | undefined;
  let bestScore = 0;

  for (const event of items) {
    let score = 0;
    const normEvent = normalizeActivityMatchText(event.message);
    if (normMatch && normEvent === normMatch) score += 100;
    else if (normMatch && (normEvent.includes(normMatch) || normMatch.includes(normEvent))) {
      score += 50;
    }

    const eventQuoted = extractQuotedActivityText(event.message);
    if (quoted && eventQuoted && quoted.toLowerCase() === eventQuoted.toLowerCase()) {
      score += 80;
    }

    if (atMs && event.created_at) {
      const diff = Math.abs(new Date(event.created_at).getTime() - atMs);
      if (diff < 120_000) score += 30;
    }

    if (score > bestScore) {
      bestScore = score;
      best = event;
    }
  }

  return bestScore >= 50 ? best : undefined;
}

export async function findPageForActivityId(activityId: string, pageSize = 20): Promise<number> {
  let page = 1;
  while (page <= 25) {
    const data = await fetchActivityFeed(page, pageSize);
    if (data.results.some((event) => event.id === activityId)) {
      return page;
    }
    if (!data.next || data.results.length === 0) break;
    page += 1;
  }
  return 1;
}

export async function findPageForActivityMatch(
  matchMessage: string,
  atIso: string | undefined,
  pageSize = 20
): Promise<number> {
  let page = 1;
  while (page <= 10) {
    const data = await fetchActivityFeed(page, pageSize);
    if (findMatchingActivityEvent(data.results, matchMessage, atIso)) {
      return page;
    }
    if (!data.next || data.results.length === 0) break;
    page += 1;
  }
  return 1;
}

export function activityEventDomId(eventId: string): string {
  return `activity-event-${eventId}`;
}
